import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  connectAuthEmulator, 
  browserLocalPersistence,
  setPersistence,
  indexedDBLocalPersistence,
  inMemoryPersistence 
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// Your Firebase configuration should be loaded from environment variables
// But for simplicity here we'll use a direct object
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase services
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const firestore = getFirestore(app);
const storage = getStorage(app);

// Set up more robust persistence for mobile environments
const setupAuthPersistence = async () => {
  try {
    // Try indexedDB first (most reliable for mobile)
    await setPersistence(auth, indexedDBLocalPersistence);
    console.log("Using indexedDB persistence");
  } catch (error) {
    try {
      // Fall back to localStorage if indexedDB fails
      console.warn("IndexedDB persistence failed, using local persistence instead");
      await setPersistence(auth, browserLocalPersistence);
      console.log("Using local persistence");
    } catch (fallbackError) {
      // Last resort - memory only (session only)
      console.error("All persistence methods failed. Using in-memory persistence (session only)");
      await setPersistence(auth, inMemoryPersistence);
    }
  }
};

// Initialize auth persistence
setupAuthPersistence().catch(error => {
  console.error("Critical error setting auth persistence:", error);
});

// Connect to emulators if in development environment
if (process.env.NODE_ENV === 'development') {
  // Connect to auth emulator if REACT_APP_USE_FIREBASE_EMULATORS is set
  if (process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true') {
    try {
      connectAuthEmulator(auth, 'http://localhost:9099');
      connectFirestoreEmulator(firestore, 'localhost', 8080);
      connectStorageEmulator(storage, 'localhost', 9199);
      console.log('Connected to Firebase emulators');
    } catch (error) {
      console.error('Error connecting to Firebase emulators:', error);
    }
  }
}

// Add an auth state change handler to log authentication state and refresh token
auth.onAuthStateChanged((user) => {
  if (user) {
    console.log('User is signed in:', user.uid);
    
    // Refresh the token periodically to prevent expiration
    // Firebase auto-refreshes but this helps ensure it happens consistently
    const refreshTokenInterval = setInterval(async () => {
      if (auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
          console.log('Auth token refreshed');
        } catch (error) {
          console.error('Error refreshing token:', error);
        }
      } else {
        clearInterval(refreshTokenInterval);
      }
    }, 10 * 60 * 1000); // Refresh every 10 minutes
  } else {
    console.log('User is signed out');
  }
});

export { app, auth, firestore, storage }; 